import { ORGANIZATION_KEY } from "@shared/constants";
import { OrgData, UserData } from "@shared/types";

import { findOrgFromPath } from "../findOrgFromPath";
import { recoverBrowserSecurityError } from "../storage/storage";

const getUserFirstOrg = (userOrgs: OrgData[]): OrgData => {
  const sortedOrgs = userOrgs.sort((a: OrgData, b: OrgData) => b.id - a.id);
  return sortedOrgs[0];
};

/**
 * Loads the Org from localstorage
 */
export const loadOrg = (
  user: UserData | undefined,
  userOrgs: OrgData[] | undefined
) => {
  if (!user || !userOrgs) return undefined;

  const firstOrg = getUserFirstOrg(userOrgs);
  /**
   * NOTE: To help with fast hydration of the UI for future page loads, we will once
   * again store org data in local storage (GROW-286). While we previously did save
   * org data to local storage, the prior implementation coupled org data with auth
   * via inclusion in the JWT. This design would cause forced user logouts when the
   * org data changed such as updating SCM provider configuration.
   * See https://github.com/semgrep/semgrep-app/pull/14117
   */
  const savedOrgData = recoverBrowserSecurityError(() =>
    localStorage.getItem(ORGANIZATION_KEY)
  );

  let savedOrg: OrgData | undefined;
  let newOrg: OrgData | undefined = findOrgFromPath(
    window.location.pathname,
    userOrgs
  );

  // if we can find the org from the path, use that org
  if (newOrg) return newOrg;

  // Try to load from local storage for previously selected org
  if (savedOrgData) {
    try {
      savedOrg = JSON.parse(savedOrgData);
    } catch {} // eslint-disable-line no-empty
  }

  if (userOrgs.some((o) => o.id === savedOrg?.id || 0)) {
    // If user has a saved org, load that org
    newOrg = savedOrg;
  } else if (firstOrg) {
    // In absence of saved org, use most-recently-added org
    newOrg = firstOrg;
  }

  return newOrg;
};
